/**
 * Row treatment for the dense ticket list — how a ticket's state turns into
 * colour.
 *
 * Kept out of the component because it is the one piece of the list with real
 * precedence rules: merged outranks breached (a merged ticket's clocks are
 * stopped, so a red row would be a lie), and breached outranks everything else.
 */

import { PRIORITY_BAR_CLASSES } from "./supportEnums";

/** Department → text colour. Full class strings (Tailwind-safe). */
export const DEPARTMENT_TEXT = {
  Billing: "text-coach-2",
  Account: "text-coach-1",
  App: "text-coach-7",
  Other: "text-coach-6",
};

/** Priority → text colour for the `PRI` column. */
export const PRIORITY_TEXT = {
  urgent: "text-danger-dark",
  high: "text-internal-text",
  normal: "text-gray-500",
  low: "text-gray-400",
};

/**
 * Visual treatment for one ticket row.
 *
 * @param {Object} ticket - Normalized ticket.
 * @returns {{ bar: string, row: string, muted: boolean, struck: boolean }}
 *   `bar` is the 3px left border, `row` the background.
 */
export const rowTreatment = (ticket) => {
  // A merged ticket is closed and its clocks are stopped — it must not carry
  // the breach tint it had a moment before the merge.
  if (ticket.mergedIntoRoomId) {
    return {
      bar: "border-l-gray-200",
      row: "bg-rail-bg",
      muted: true,
      struck: true,
    };
  }
  if (ticket.breached) {
    return {
      bar: "border-l-danger",
      row: "bg-danger-row",
      muted: false,
      struck: false,
    };
  }
  const snoozedOrWaiting =
    Boolean(ticket.snoozedUntil) || ticket.waitingOn === "customer";
  return {
    bar: PRIORITY_BAR_CLASSES[ticket.priority]
      ? PRIORITY_BAR_CLASSES[ticket.priority].replace("bg-", "border-l-")
      : "border-l-gray-300",
    row: snoozedOrWaiting ? "bg-white" : "bg-white",
    muted: snoozedOrWaiting,
    struck: false,
  };
};

/**
 * The compact SLA cell: what the clock says, and how loudly.
 *
 * `1st +4:12` means the first-response deadline blew four hours twelve ago;
 * `next 0:12` is twelve minutes left on the reply clock. A paused clock reads
 * `waiting`, a snoozed ticket `snoozed`, a merged one `merged`.
 *
 * @param {Object} ticket - Normalized ticket.
 * @param {number} [now]
 * @returns {{ label: string, className: string }}
 */
export const slaCell = (ticket, now = Date.now()) => {
  const neutral = "bg-gray-100 text-gray-500";
  if (ticket.mergedIntoRoomId) return { label: "merged", className: neutral };
  if (ticket.snoozedUntil && Date.parse(ticket.snoozedUntil) > now) {
    return { label: "snoozed", className: neutral };
  }
  if (ticket.ticketStatus === "RESOLVED" || ticket.status === "resolved") {
    return { label: "resolved", className: "bg-success-bg text-success" };
  }
  if (ticket.waitingOn === "customer") return { label: "waiting", className: neutral };

  const replied = Boolean(ticket.firstRepliedAt);
  const dueIso = replied ? ticket.nextResponseDueAt : ticket.firstReplyDueAt;
  const prefix = replied ? "next" : "1st";

  if (ticket.breached || (dueIso && Date.parse(dueIso) <= now)) {
    // Overshoot is the number that matters once a clock has blown.
    const over = dueIso ? formatClock(now - Date.parse(dueIso)) : null;
    return {
      label: over ? `${prefix} +${over}` : "breached",
      className: "bg-danger text-white",
    };
  }
  if (!dueIso) return { label: "—", className: neutral };

  const left = Date.parse(dueIso) - now;
  if (Number.isNaN(left)) return { label: "—", className: neutral };
  // Under half an hour is the point where an agent should act now, not later.
  const urgent = left < 30 * 60 * 1000;
  return {
    label: `${prefix} ${formatClock(left)}`,
    className: urgent
      ? "bg-internal-bg text-internal-text"
      : "bg-success-bg text-success",
  };
};

/**
 * `h:mm` from a millisecond span.
 * @param {number} ms
 * @returns {string}
 */
export function formatClock(ms) {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  return `${Math.floor(totalMin / 60)}:${String(totalMin % 60).padStart(2, "0")}`;
}
